/**
 * Templates Page (Phase 12.3 — formerly Assessment Schedules)
 *
 * Unified page for managing all assessment templates: SOP-based schedules,
 * AI-generated KPI spot checks, and checklist verifications.
 * Features create/edit modal, pause/resume, source type badges, and status indicators.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  CalendarClock,
  Plus,
  Search,
  AlertCircle,
  Loader2,
  Pencil,
  Trash2,
  X,
  ChevronDown,
  Play,
  Pause,
  FileText,
  Camera,
  Building2,
  Clock,
  RefreshCw,
  MessageSquare,
  CheckSquare,
  ExternalLink,
  MoreVertical,
  Gauge,
  ListChecks,
} from 'lucide-react';
import {
  useAssessmentSchedules,
  useDeleteSchedule,
  useCreateSchedule,
  useUpdateSchedule,
  usePauseSchedule,
  useResumeSchedule,
  formatScheduleType,
  formatTimeWindow,
  parseDaysOfWeek,
  formatDaysOfWeek,
  type AssessmentSchedule,
  type CreateAssessmentSchedule,
  type ScheduleType,
  type SelectionMode,
} from '../../hooks/useAssessmentSchedules';
import { useSopLookup, useSopSectionsLookup } from '../../hooks/useSOPs';
import { useCameraLookup } from '../../hooks/useCameras';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { useChecklistsLookup } from '../../hooks/useChecklists';
import { useKpiLookup } from '../../hooks/useKpi';
import { useCurrentUser } from '../../hooks/useUsers';
import { useNavigate } from 'react-router-dom';
import { SourceTypeBadge } from '../../components/SourceTypeBadge';
import { KpiConversationChat } from '../../components/kpi/KpiConversationChat';
import { ScopeChipPicker } from '../../components/kpi/ScopeChipPicker';
import { useApi } from '../../lib/hooks/useApi';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

export function AssessmentSchedulesPage() {
  useDocumentTitle('Templates');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  // Templates are manageable by manager+ (backend: requireRole('manager'))
  const isAdmin = userRole === 'admin' || isSuperAdmin || userRole === 'trainer' || userRole === 'manager';

  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<AssessmentSchedule | null>(null);

  const { data: schedulesData, isLoading, error } = useAssessmentSchedules();
  const deleteMutation = useDeleteSchedule();
  const pauseMutation = usePauseSchedule();
  const resumeMutation = useResumeSchedule();

  // Filter schedules by search query
  // Note: useApi unwraps the { success, data } envelope, so schedulesData is the array directly
  const filteredSchedules = (schedulesData ?? []).filter((schedule) =>
    schedule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    schedule.sopTitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => {
    setEditingSchedule(null);
    setIsFormOpen(true);
  };

  const handleEdit = (schedule: AssessmentSchedule) => {
    setEditingSchedule(schedule);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handlePause = async (id: string) => {
    await pauseMutation.mutateAsync(id);
  };

  const handleResume = async (id: string) => {
    await resumeMutation.mutateAsync(id);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingSchedule(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50">
            <CalendarClock size={24} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Templates
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Automated assessment templates for SOP compliance and KPI tracking
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            New Template
          </button>
        )}
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[280px]">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
              w-full rounded-xl border border-gray-200 bg-white
              pl-11 pr-4 py-2.5 text-sm
              placeholder:text-gray-400
              focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              transition-all duration-200
            "
          />
        </div>
      </div>

      {/* Content */}
      {error ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Failed to load templates
            </p>
            <p className="text-sm text-red-600">
              Please try refreshing the page or contact support if the issue persists.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="text-guild-green animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading templates...</p>
        </div>
      ) : filteredSchedules.length > 0 ? (
        <ScheduleList
          schedules={filteredSchedules}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onPause={handlePause}
          onResume={handleResume}
          isDeleting={deleteMutation.isPending}
          isPausing={pauseMutation.isPending}
          isResuming={resumeMutation.isPending}
        />
      ) : (
        <EmptyState
          searchQuery={searchQuery}
          isAdmin={isAdmin}
          onCreateClick={handleCreate}
        />
      )}

      {/* Form Dialog */}
      {isFormOpen && (
        <ScheduleForm
          schedule={editingSchedule}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// ============================================
// SCHEDULE LIST COMPONENT
// ============================================

interface ScheduleListProps {
  schedules: AssessmentSchedule[];
  isAdmin: boolean;
  onEdit: (schedule: AssessmentSchedule) => void;
  onDelete: (id: string) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  isDeleting: boolean;
  isPausing: boolean;
  isResuming: boolean;
}

function ScheduleList({
  schedules,
  isAdmin,
  onEdit,
  onDelete,
  onPause,
  onResume,
  isDeleting,
  isPausing,
  isResuming,
}: ScheduleListProps) {
  const listNavigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-x-auto">
      <table className="w-full min-w-[700px]">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Template
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Source
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              SOP
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Frequency
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Time Window
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Next Run
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            {isAdmin && (
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {schedules.map((schedule) => (
            <tr
              key={schedule.id}
              className={`
                group transition-colors
                ${schedule.isActive ? 'hover:bg-gray-50/50' : 'bg-gray-50/30'}
              `}
            >
              {/* Name */}
              <td className="px-4 py-3.5">
                <div className="flex items-center gap-3 max-w-[220px]">
                  <div
                    className={`
                      flex h-9 w-9 shrink-0 items-center justify-center rounded-lg
                      ${schedule.isActive ? 'bg-indigo-50' : 'bg-gray-100'}
                    `}
                  >
                    <CalendarClock
                      size={16}
                      className={schedule.isActive ? 'text-indigo-600' : 'text-gray-400'}
                    />
                  </div>
                  <div className="min-w-0">
                    <span
                      className={`
                        text-sm font-semibold block truncate
                        ${schedule.isActive ? 'text-gray-900' : 'text-gray-400'}
                      `}
                      title={schedule.name}
                    >
                      {schedule.name}
                    </span>
                    {/* Phase 34: lens indicator — existing (NULL) rows render unchanged */}
                    {schedule.sourceNumber != null && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                        <Camera size={11} />
                        Lens {schedule.sourceNumber}
                      </span>
                    )}
                    {/* Phase 36.4 (S6): Job scope indicator. Unscoped (NULL) rows
                        render nothing, so every pre-36.4 template looks unchanged.
                        Visible at a glance because a scoped template that stops
                        producing assessments is debugged from this column first. */}
                    {schedule.scopeSectionIds && schedule.scopeSectionIds.length > 0 && (
                      <span
                        className={`
                          inline-flex items-center gap-1 text-xs text-gray-400
                          ${schedule.sourceNumber != null ? 'ml-2' : ''}
                        `}
                        title="This template only scores the selected Jobs"
                      >
                        <ListChecks size={11} />
                        {schedule.scopeSectionIds.length}{' '}
                        {schedule.scopeSectionIds.length === 1 ? 'Job' : 'Jobs'}
                      </span>
                    )}
                    {schedule.description && (
                      <span className="text-xs text-gray-400 line-clamp-1">
                        {schedule.description}
                      </span>
                    )}
                  </div>
                </div>
              </td>

              {/* Source Type (Phase 12.3) */}
              <td className="px-4 py-3.5 whitespace-nowrap">
                <SourceTypeBadge sourceType={schedule.sourceType ?? 'sop'} />
              </td>

              {/* SOP / KPI Name */}
              <td className="px-4 py-3.5">
                <div className="flex items-center gap-2">
                  {schedule.sourceType === 'kpi' ? (
                    <Gauge
                      size={14}
                      className={schedule.isActive ? 'text-amber-500' : 'text-gray-300'}
                    />
                  ) : (
                    <FileText
                      size={14}
                      className={schedule.isActive ? 'text-gray-400' : 'text-gray-300'}
                    />
                  )}
                  <span
                    className={`
                      text-sm max-w-[150px] truncate
                      ${schedule.isActive ? 'text-gray-600' : 'text-gray-400'}
                    `}
                  >
                    {schedule.sourceType === 'kpi'
                      ? (schedule.kpiDefinitionName || <span className="italic text-gray-300">No KPI</span>)
                      : (schedule.sopTitle || <span className="italic text-gray-300">None</span>)
                    }
                  </span>
                </div>
              </td>

              {/* Type */}
              <td className="px-4 py-3.5 whitespace-nowrap">
                <span
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium
                    ${schedule.scheduleType === 'daily'
                      ? 'bg-blue-50 text-blue-700'
                      : schedule.scheduleType === 'weekly'
                        ? 'bg-purple-50 text-purple-700'
                        : 'bg-amber-50 text-amber-700'
                    }
                  `}
                >
                  {formatScheduleType(schedule.scheduleType)}
                </span>
              </td>

              {/* Frequency */}
              <td className="px-4 py-3.5 whitespace-nowrap">
                <span
                  className={`
                    text-sm
                    ${schedule.isActive ? 'text-gray-600' : 'text-gray-400'}
                  `}
                >
                  {schedule.frequency}x per {schedule.scheduleType === 'daily' ? 'day' : 'week'}
                </span>
              </td>

              {/* Time Window */}
              <td className="px-4 py-3.5 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  <Clock
                    size={14}
                    className={schedule.isActive ? 'text-gray-400' : 'text-gray-300'}
                  />
                  <span
                    className={`
                      text-sm
                      ${schedule.isActive ? 'text-gray-600' : 'text-gray-400'}
                    `}
                  >
                    {formatTimeWindow(schedule.startHour, schedule.endHour, schedule.timezone)}
                  </span>
                </div>
              </td>

              {/* Next Run */}
              <td className="px-4 py-3.5 whitespace-nowrap">
                {schedule.nextRunAt ? (
                  <span
                    className={`
                      text-sm
                      ${schedule.isActive ? 'text-gray-600' : 'text-gray-400'}
                    `}
                  >
                    {new Date(schedule.nextRunAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                ) : (
                  <span className="text-gray-300 italic text-sm">Not scheduled</span>
                )}
              </td>

              {/* Status */}
              <td className="px-4 py-3.5 whitespace-nowrap">
                <span
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold
                    ${schedule.isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-500'
                    }
                  `}
                >
                  <span
                    className={`
                      h-1.5 w-1.5 rounded-full
                      ${schedule.isActive ? 'bg-emerald-500' : 'bg-gray-400'}
                    `}
                  />
                  {schedule.isActive ? 'Active' : 'Paused'}
                </span>
              </td>

              {/* Actions */}
              {isAdmin && (
                <td className="px-4 py-3.5 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    {/* Pause/Resume — primary action stays visible */}
                    {schedule.isActive ? (
                      <button
                        onClick={() => onPause(schedule.id)}
                        disabled={isPausing}
                        className="
                          inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5
                          text-sm font-medium text-amber-600
                          hover:bg-amber-50 hover:text-amber-700
                          disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors
                        "
                        title="Pause template"
                      >
                        <Pause size={14} />
                        <span className="hidden lg:inline">Pause</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => onResume(schedule.id)}
                        disabled={isResuming}
                        className="
                          inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5
                          text-sm font-medium text-emerald-600
                          hover:bg-emerald-50 hover:text-emerald-700
                          disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors
                        "
                        title="Resume template"
                      >
                        <Play size={14} />
                        <span className="hidden lg:inline">Resume</span>
                      </button>
                    )}

                    {/* More actions dropdown — uses fixed positioning to escape overflow-x-auto clipping */}
                    <div className="relative">
                      <button
                        onClick={() => {
                          if (openMenuId === schedule.id) {
                            setOpenMenuId(null);
                          } else {
                            setOpenMenuId(schedule.id);
                          }
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                        title="More actions"
                        data-menu-trigger={schedule.id}
                      >
                        <MoreVertical size={16} />
                      </button>

                      {openMenuId === schedule.id && (() => {
                        const trigger = document.querySelector(`[data-menu-trigger="${schedule.id}"]`);
                        const rect = trigger?.getBoundingClientRect();
                        return (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setOpenMenuId(null)}
                            />
                            <div
                              className="fixed z-50 w-48 rounded-xl bg-white border border-gray-100 shadow-xl py-1.5"
                              style={{
                                top: rect ? rect.bottom + 4 : 0,
                                left: rect ? rect.right - 192 : 0,
                              }}
                            >
                              {/* Edit Checklist Items (for checklist source type) */}
                              {schedule.sourceType === 'checklist' && schedule.checklistId && (
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    listNavigate('/settings/checklists');
                                  }}
                                  className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-teal-700 hover:bg-teal-50 transition-colors"
                                >
                                  <ExternalLink size={14} />
                                  Edit Items
                                </button>
                              )}

                              {/* Edit */}
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  onEdit(schedule);
                                }}
                                className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Pencil size={14} />
                                Edit
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => {
                                  setOpenMenuId(null);
                                  onDelete(schedule.id);
                                }}
                                disabled={isDeleting}
                                className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// SCHEDULE FORM COMPONENT
// ============================================

interface ScheduleFormProps {
  schedule: AssessmentSchedule | null;
  onClose: () => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

function ScheduleForm({ schedule, onClose }: ScheduleFormProps) {
  const isEdit = !!schedule;

  const api = useApi();

  // Phase 12.3 + 24.3: Source type state
  type SourceType = 'sop' | 'ai_generated' | 'checklist' | 'kpi';
  const [sourceType, setSourceType] = useState<SourceType>(
    (schedule?.sourceType as SourceType) ?? 'sop'
  );
  const [checklistId, setChecklistId] = useState(schedule?.checklistId ?? '');
  const [kpiDefinitionId, setKpiDefinitionId] = useState(schedule?.kpiDefinitionId ?? '');
  const kpiTemplateCode = 'general'; // Always use general template for AI conversation
  const [showChat, setShowChat] = useState(false);
  const [generatedSopId, setGeneratedSopId] = useState<string | null>(
    schedule?.sourceType === 'ai_generated' && schedule?.sopId ? schedule.sopId : null
  );
  const [generatedSopTitle, setGeneratedSopTitle] = useState<string | null>(
    schedule?.sourceType === 'ai_generated' && schedule?.sopTitle ? schedule.sopTitle : null
  );

  // Phase 35.7: the inline checklist-creation mode was removed — 'checklist'
  // stopped being a creatable source type in Phase 24.3 (pipeline silently
  // degrades it to observation mode; full deprecation deferred to 24.6.6).
  // Existing checklist-type templates remain editable via the select dropdown.

  // Form state
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [name, setName] = useState(schedule?.name ?? '');
  const [description, setDescription] = useState(schedule?.description ?? '');
  const [sopId, setSopId] = useState(schedule?.sopId ?? '');
  const [cameraId, setCameraId] = useState(schedule?.cameraId ?? '');
  // Phase 34: target lens for multi-sensor cameras (D1: default = Lens 1, visible in the dropdown)
  const [sourceNumber, setSourceNumber] = useState<number>(schedule?.sourceNumber ?? 1);
  // Phase 36.4: Job (section) allowlist. Empty = whole SOP, i.e. every pre-36.4
  // template. Held as [] rather than null because ScopeChipPicker takes string[];
  // the empty -> null conversion happens once, in the submit payload.
  const [scopeSectionIds, setScopeSectionIds] = useState<string[]>(schedule?.scopeSectionIds ?? []);
  const [departmentId, setDepartmentId] = useState(schedule?.departmentId ?? '');
  const [scheduleType, setScheduleType] = useState<ScheduleType>(schedule?.scheduleType ?? 'daily');
  const [frequency, setFrequency] = useState(schedule?.frequency ?? 1);
  const [startHour, setStartHour] = useState(schedule?.startHour ?? 8);
  const [endHour, setEndHour] = useState(schedule?.endHour ?? 17);
  const [selectedDays, setSelectedDays] = useState<number[]>(
    schedule?.daysOfWeek ? parseDaysOfWeek(schedule.daysOfWeek) : [1, 2, 3, 4, 5]
  );
  const [timezone] = useState(schedule?.timezone ?? browserTimezone);
  // Operator selection removed — face ID determines operator during analysis
  const [isActive, setIsActive] = useState(schedule?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  // Fetch dropdown data
  const { data: sops } = useSopLookup();
  const { data: cameras } = useCameraLookup();
  const { data: departments } = useDepartmentLookup();
  const { data: checklists } = useChecklistsLookup();
  const { data: kpiDefinitionsLookup } = useKpiLookup();
  // Phase 36.4: the Jobs of the selected SOP. Only 'sop' templates can carry a
  // scope — the API 400s a scope on a KPI template — so the lookup stays
  // disabled for every other source type.
  const sectionsLookup = useSopSectionsLookup(sourceType === 'sop' ? sopId || null : null);

  // Phase 34: the selected camera's sourceCount drives the lens dropdown
  const selectedCamera = cameras?.find((c) => c.id === cameraId);

  // Phase 36.4: picker options. Stored ids the lookup does not know about are
  // merged back in as isActive:false so ScopeChipPicker renders them as
  // removable-but-not-re-addable "(inactive)" chips — the same stale-value
  // affordance as KpiLibraryPage's mergeWithStale(). That case is reachable:
  // a SOP reprocess deletes and recreates every section with fresh UUIDs.
  // While the lookup is unresolved EVERY stored id looks unknown, so the stale
  // label is only applied once there is a resolved list to be absent from.
  const jobOptions = useMemo(() => {
    const rows = sectionsLookup.data;
    const known = new Set((rows ?? []).map((r) => r.id));
    const options: Array<{ id: string; name: string; isActive?: boolean }> = (rows ?? []).map((r) => ({
      id: r.id,
      // UI terminology: sopSections render as "Jobs" (critical-patterns.md).
      // The "no steps" suffix is the pre-submit warning for the API's
      // "have no steps and cannot be scored" 400.
      name:
        r.stepCount === 0
          ? `Job ${r.sectionNumber}: ${r.title} — no steps`
          : `Job ${r.sectionNumber}: ${r.title}`,
    }));
    for (const id of scopeSectionIds) {
      if (known.has(id)) continue;
      options.push(
        rows
          ? { id, name: 'Job no longer in this SOP', isActive: false }
          : { id, name: 'Loading…' }
      );
    }
    return options;
  }, [sectionsLookup.data, scopeSectionIds]);

  // Phase 36.4: the two selections the API will reject, surfaced before Save.
  const selectedWithoutSteps = (sectionsLookup.data ?? []).filter(
    (r) => r.stepCount === 0 && scopeSectionIds.includes(r.id)
  );
  const staleScopeCount = sectionsLookup.data
    ? scopeSectionIds.filter((id) => !sectionsLookup.data.some((r) => r.id === id)).length
    : 0;

  const createMutation = useCreateSchedule();
  const updateMutation = useUpdateSchedule();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when schedule changes
  useEffect(() => {
    setName(schedule?.name ?? '');
    setDescription(schedule?.description ?? '');
    setSopId(schedule?.sopId ?? '');
    setCameraId(schedule?.cameraId ?? '');
    setSourceNumber(schedule?.sourceNumber ?? 1); // Phase 34
    setScopeSectionIds(schedule?.scopeSectionIds ?? []); // Phase 36.4
    setDepartmentId(schedule?.departmentId ?? '');
    setScheduleType(schedule?.scheduleType ?? 'daily');
    setFrequency(schedule?.frequency ?? 1);
    setStartHour(schedule?.startHour ?? 8);
    setEndHour(schedule?.endHour ?? 17);
    setSelectedDays(schedule?.daysOfWeek ? parseDaysOfWeek(schedule.daysOfWeek) : [1, 2, 3, 4, 5]);
    setIsActive(schedule?.isActive ?? true);
    setSourceType((schedule?.sourceType as SourceType) ?? 'sop');
    setChecklistId(schedule?.checklistId ?? '');
    setKpiDefinitionId(schedule?.kpiDefinitionId ?? '');
    setGeneratedSopId(schedule?.sourceType === 'ai_generated' && schedule?.sopId ? schedule.sopId : null);
    setGeneratedSopTitle(schedule?.sourceType === 'ai_generated' && schedule?.sopTitle ? schedule.sopTitle : null);
    setShowChat(false);
    setError(null);
  }, [schedule]);

  const handleDayToggle = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    // Phase 12.3 + 24.3: Conditional validation based on source type
    if (sourceType === 'sop' && !sopId) {
      setError('SOP is required for SOP-based templates');
      return;
    }
    if (sourceType === 'ai_generated' && !generatedSopId) {
      setError('Please complete the AI conversation to generate an SOP first');
      return;
    }
    // Phase 35.7: 'checklist' is only reachable when editing a pre-existing
    // checklist-type template (not creatable since Phase 24.3)
    if (sourceType === 'checklist' && !checklistId) {
      setError('Checklist is required for checklist-based templates');
      return;
    }
    if (sourceType === 'kpi' && !kpiDefinitionId) {
      setError('KPI is required for KPI-based templates');
      return;
    }

    if (startHour >= endHour) {
      setError('End hour must be after start hour');
      return;
    }

    if (selectedDays.length === 0) {
      setError('At least one day must be selected');
      return;
    }

    try {
      // Camera is required — scheduler will auto-pause templates without one
      if (!cameraId) {
        setError('Camera is required. The scheduler needs a camera to capture and analyze video.');
        return;
      }

      // Resolve sopId based on source type
      const effectiveSopId = sourceType === 'ai_generated' ? generatedSopId ?? undefined
        : sourceType === 'sop' ? sopId || undefined
        : undefined;

      const data: CreateAssessmentSchedule = {
        name: name.trim(),
        description: description.trim() || undefined,
        sopId: effectiveSopId,
        cameraId: cameraId || undefined,
        // Phase 34: number = target that lens (multi-lens camera); null = clear
        // (single-lens camera or no camera — server stores NULL = lens 1);
        // undefined = leave the stored lens untouched on update. Sent while the
        // camera lookup is unresolved, AND when the stored camera is absent from
        // the lookup (inactive camera — /lookup filters isActive; the lens UI is
        // hidden then, and a null here would silently clear the lens to NULL =
        // lens 1, which never trips the D5 lens auto-pause).
        sourceNumber: !cameras || (!!cameraId && !selectedCamera)
          ? undefined
          : selectedCamera && selectedCamera.sourceCount > 1
            ? sourceNumber
            : null,
        // Phase 36.4: null = whole SOP (also the documented per-template
        // rollback lever); [] is NEVER sent, the API rejects it with a 400
        // (Zod .min(1)). Non-'sop' templates always clear: there is no picker
        // for them, and the API 400s a scope on a KPI template — sending null
        // is what lets a scoped SOP template be converted to KPI without the
        // stored scope surviving the conversion invisibly.
        //
        // DELIBERATE DEVIATION from the blueprint (chunk 6 step 6), which adds a
        // third arm: `!sectionsLookup.data ? undefined` — "lookup unresolved,
        // never clobber", by analogy with the Phase 34 lens field above. That
        // analogy does not hold here and the arm is a net negative:
        //   - sourceNumber needs it because its state defaults to a SYNTHETIC 1
        //     when the camera lookup is unresolved, so sending it would overwrite
        //     a real stored lens with a guess. scopeSectionIds has no synthetic
        //     default — it is seeded from schedule.scopeSectionIds and is only
        //     ever changed by an explicit user action. The Jobs lookup supplies
        //     chip LABELS, never the selection, so an unresolved lookup can never
        //     be the reason the array is empty.
        //   - It breaks the SOP-change flow the reset above exists to fix: the
        //     reset empties the scope, the lookup restarts for the new sopId, and
        //     saving inside that window would send `undefined` -> the server falls
        //     back to the STORED scope, validates it against the NEW SOP, and 400s
        //     with "do not belong to this SOP". That is the blueprint's own
        //     chunk-6 verification step 4 ("change the SOP ... no 400") failing.
        //   - In the same window it silently discards a chip the trainer removed.
        // So: send what the trainer can see, always.
        scopeSectionIds: sourceType !== 'sop'
          ? null
          : scopeSectionIds.length === 0 ? null : scopeSectionIds,
        departmentId: departmentId || undefined,
        sourceType,
        checklistId: sourceType === 'checklist' ? checklistId || undefined : undefined,
        kpiDefinitionId: sourceType === 'kpi' ? kpiDefinitionId || undefined : undefined,
        scheduleType,
        frequency,
        startHour,
        endHour,
        daysOfWeek: formatDaysOfWeek(selectedDays),
        timezone,
        selectionMode: 'random' as SelectionMode, // Default — operator selection removed, face ID determines operator
        isActive,
      };

      if (isEdit && schedule) {
        await updateMutation.mutateAsync({ id: schedule.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50">
              <CalendarClock size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEdit ? 'Edit Template' : 'New Template'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEdit ? 'Update template settings' : 'Configure automated spot-check assessments'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Phase 24.3: Simplified Source Type Tabs — SOP and KPI */}
            {!isEdit && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Source Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { value: 'sop' as const, label: 'SOP', icon: FileText, desc: 'Assess against an SOP' },
                    { value: 'kpi' as const, label: 'KPI', icon: Gauge, desc: 'Track a specific metric' },
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setSourceType(opt.value); setShowChat(false); setGeneratedSopId(null); setKpiDefinitionId(''); }}
                      className={`
                        flex flex-col items-center gap-1.5 rounded-xl border-2 px-3 py-3 text-center transition-all
                        ${sourceType === opt.value
                          ? 'border-guild-green bg-guild-green/5 text-guild-green'
                          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                        }
                      `}
                    >
                      <opt.icon size={18} />
                      <span className="text-xs font-semibold">{opt.label}</span>
                      <span className="text-[10px] font-normal opacity-70">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Morning Safety Check"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Description Field */}
            <div>
              <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                Description
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this template..."
                rows={2}
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  resize-none
                  transition-all duration-200
                "
                disabled={isPending}
              />
            </div>

            {/* SOP Field — only for 'sop' source type */}
            {sourceType === 'sop' && (
              <div>
                <label htmlFor="sopId" className="block text-sm font-semibold text-gray-700 mb-2">
                  SOP <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <select
                    id="sopId"
                    value={sopId}
                    // Phase 36.4: clearing the scope on a SOP change is
                    // mandatory, not tidiness — the API validates the scope as a
                    // subset of the template's SOP, so carrying the old Jobs over
                    // would 400 with "do not belong to this SOP" and the trainer
                    // would have no way to see why. Mirrors the camera -> lens
                    // reset relationship in the Phase 34 field below.
                    onChange={(e) => {
                      setSopId(e.target.value);
                      setScopeSectionIds([]);
                    }}
                    disabled={isPending}
                    className="
                      w-full appearance-none rounded-xl border border-gray-200 bg-white
                      py-3 pl-10 pr-10 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    <option value="">Select an SOP...</option>
                    {sops?.map((sop) => (
                      <option key={sop.id} value={sop.id}>
                        {sop.title}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>
            )}

            {/* Phase 36.4 (S6): Job scope — only for 'sop' templates, and only
                once a SOP is picked. Deliberately absent for 'kpi': the API 400s
                a scope on a KPI template because there is no SOP for it to be a
                subset of. */}
            {sourceType === 'sop' && !!sopId && (
              <div>
                <ScopeChipPicker
                  label="Jobs"
                  options={jobOptions}
                  selectedIds={scopeSectionIds}
                  onChange={setScopeSectionIds}
                  required={false}
                  placeholder={
                    sectionsLookup.isLoading
                      ? 'Loading Jobs…'
                      : jobOptions.length === 0
                        ? 'No Jobs yet'
                        : 'Select Jobs…'
                  }
                  helpText="Optional — empty scores every Job"
                  disabled={isPending}
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Naming the Jobs this camera actually watches tells the analysis
                  which processes to look for. Leave empty to consider every Job in
                  the SOP.
                </p>
                {sectionsLookup.error && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle size={12} />
                    Couldn't load this SOP's Jobs — try reopening the form.
                  </p>
                )}
                {/* Both lines below pre-empt a specific API rejection so the
                    trainer sees the cause next to the picker rather than as a
                    bare error banner after Save. */}
                {selectedWithoutSteps.length > 0 && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    {selectedWithoutSteps
                      .map((r) => `Job ${r.sectionNumber}: ${r.title}`)
                      .join(', ')}{' '}
                    {selectedWithoutSteps.length === 1 ? 'has' : 'have'} no steps and
                    cannot be scored. Remove {selectedWithoutSteps.length === 1 ? 'it' : 'them'} to save.
                  </p>
                )}
                {staleScopeCount > 0 && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    {staleScopeCount} selected {staleScopeCount === 1 ? 'Job is' : 'Jobs are'} no
                    longer part of this SOP — it was reprocessed since this template was
                    saved. Remove the {staleScopeCount === 1 ? 'chip' : 'chips'} marked
                    (inactive) and pick again.
                  </p>
                )}
              </div>
            )}

            {/* AI Generated — for 'ai_generated' source type */}
            {sourceType === 'ai_generated' && (
              <div>
                {generatedSopId ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <p className="text-sm font-semibold text-emerald-800">SOP Generated: {generatedSopTitle}</p>
                      <button
                        type="button"
                        onClick={() => { setGeneratedSopId(null); setGeneratedSopTitle(null); setShowChat(false); }}
                        className="text-xs text-emerald-600 hover:underline mt-1"
                      >
                        Regenerate
                      </button>
                    </div>

                  </div>
                ) : showChat ? (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <KpiConversationChat
                      templateCode={kpiTemplateCode || 'general'}
                      templateName="SOP Generator"
                      onSopGenerated={async (sopStructure) => {
                        if (!sopStructure) return;
                        try {
                          // useApi unwraps { success, data } envelope, so result is { sopId, title } directly
                          const result = await api.post<{ sopId: string; title: string }>(
                            '/api/assessment-schedules/generate-sop',
                            { title: sopStructure.title, sections: sopStructure.sections }
                          );
                          setGeneratedSopId(result.sopId);
                          setGeneratedSopTitle(result.title);
                          setError(null);
                          if (!name) setName(sopStructure.title);
                        } catch {
                          setError('Failed to save generated SOP');
                        }
                      }}
                      onCancel={() => setShowChat(false)}
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">
                      The AI will ask you questions about your process, then generate a structured SOP for video-based assessment.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowChat(true)}
                      className="
                        inline-flex items-center gap-2 rounded-xl border border-guild-green/30 bg-guild-green/5
                        px-4 py-2.5 text-sm font-semibold text-guild-green
                        hover:bg-guild-green/10 transition-all
                      "
                    >
                      <MessageSquare size={16} />
                      Start AI Conversation
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Checklist — only reachable when EDITING a pre-existing checklist-type
                template. Phase 35.7: 'checklist' is not offered as a creatable source
                type (tabs are SOP/KPI since Phase 24.3 — the pipeline silently degrades
                checklist templates to observation mode; deprecation deferred to 24.6.6),
                so the inline checklist-creation flow was removed. */}
            {sourceType === 'checklist' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Checklist <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <CheckSquare size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <select id="checklistId" value={checklistId} onChange={(e) => setChecklistId(e.target.value)} disabled={isPending}
                    className="w-full appearance-none rounded-xl border border-gray-200 bg-white py-3 pl-10 pr-10 text-sm focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200">
                    <option value="">Select a checklist...</option>
                    {checklists?.map((cl) => (
                      <option key={cl.id} value={cl.id}>{cl.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* KPI Selection — for 'kpi' source type (Phase 24.3) */}
            {sourceType === 'kpi' && (
              <div>
                <label htmlFor="kpiDefinitionId" className="block text-sm font-semibold text-gray-700 mb-2">
                  KPI <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Gauge
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <select
                    id="kpiDefinitionId"
                    value={kpiDefinitionId}
                    onChange={(e) => {
                      setKpiDefinitionId(e.target.value);
                      // Auto-populate name from KPI selection if name is empty
                      if (!name && e.target.value) {
                        const selected = kpiDefinitionsLookup?.find(k => k.id === e.target.value);
                        if (selected) setName(selected.name);
                      }
                    }}
                    disabled={isPending}
                    className="
                      w-full appearance-none rounded-xl border border-gray-200 bg-white
                      py-3 pl-10 pr-10 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    <option value="">Select a KPI...</option>
                    {kpiDefinitionsLookup?.map((kpi) => (
                      <option key={kpi.id} value={kpi.id}>
                        {kpi.name}{kpi.category ? ` (${kpi.category})` : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
                {kpiDefinitionsLookup?.length === 0 && (
                  <p className="mt-2 text-xs text-amber-600">
                    No KPIs available. Create a KPI in the Library first.
                  </p>
                )}
              </div>
            )}

            {/* Camera & Department Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Camera Field */}
              <div>
                <label htmlFor="cameraId" className="block text-sm font-semibold text-gray-700 mb-2">
                  Camera
                  <span className="font-normal text-red-400 ml-1">*</span>
                </label>
                <div className="relative">
                  <Camera
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <select
                    id="cameraId"
                    value={cameraId}
                    onChange={(e) => {
                      setCameraId(e.target.value);
                      setSourceNumber(1); // Phase 34: reset lens when the camera changes
                    }}
                    disabled={isPending}
                    className="
                      w-full appearance-none rounded-xl border border-gray-200 bg-white
                      py-3 pl-10 pr-10 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    <option value="">Any camera</option>
                    {cameras?.map((camera) => (
                      <option key={camera.id} value={camera.id}>
                        {camera.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>

              {/* Department Field */}
              <div>
                <label htmlFor="departmentId" className="block text-sm font-semibold text-gray-700 mb-2">
                  Department
                  <span className="font-normal text-gray-400 ml-1">(optional)</span>
                </label>
                <div className="relative">
                  <Building2
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <select
                    id="departmentId"
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    disabled={isPending}
                    className="
                      w-full appearance-none rounded-xl border border-gray-200 bg-white
                      py-3 pl-10 pr-10 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    <option value="">Any department</option>
                    {departments?.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>
            </div>

            {/* Phase 34: Camera Lens — only shown for multi-sensor cameras (D1: default Lens 1) */}
            {selectedCamera && selectedCamera.sourceCount > 1 && (
              <div>
                <label htmlFor="sourceNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                  Camera Lens
                </label>
                <div className="relative">
                  <Camera
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <select
                    id="sourceNumber"
                    value={sourceNumber}
                    onChange={(e) => setSourceNumber(parseInt(e.target.value))}
                    disabled={isPending}
                    className="
                      w-full appearance-none rounded-xl border border-gray-200 bg-white
                      py-3 pl-10 pr-10 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    {Array.from({ length: selectedCamera.sourceCount }, (_, i) => i + 1).map((lens) => (
                      <option key={lens} value={lens}>
                        Lens {lens}
                      </option>
                    ))}
                    {sourceNumber > selectedCamera.sourceCount && (
                      <option value={sourceNumber}>Lens {sourceNumber} (no longer available)</option>
                    )}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                  This camera records {selectedCamera.sourceCount} lenses. Assessments will score the selected lens.
                </p>
              </div>
            )}

            {/* Schedule Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Schedule Type
              </label>
              <div className="flex gap-3">
                {(['daily', 'weekly'] as ScheduleType[]).map((type) => (
                  <label
                    key={type}
                    className={`
                      flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all
                      ${scheduleType === type
                        ? 'border-guild-green bg-guild-green/5 text-guild-green'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="scheduleType"
                      value={type}
                      checked={scheduleType === type}
                      onChange={() => setScheduleType(type)}
                      className="sr-only"
                    />
                    <RefreshCw size={14} />
                    <span className="text-sm font-medium">{formatScheduleType(type)}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label htmlFor="frequency" className="block text-sm font-semibold text-gray-700 mb-2">
                Frequency
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="frequency"
                  type="number"
                  min={1}
                  max={100}
                  value={frequency}
                  onChange={(e) => setFrequency(parseInt(e.target.value) || 1)}
                  className="
                    w-20 rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm text-center
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                  disabled={isPending}
                />
                <span className="text-sm text-gray-500">
                  times per {scheduleType === 'daily' ? 'day' : 'week'}
                </span>
              </div>
            </div>

            {/* Time Window */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Time Window
              </label>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Clock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <select
                    value={startHour}
                    onChange={(e) => setStartHour(parseInt(e.target.value))}
                    disabled={isPending}
                    className="
                      w-full appearance-none rounded-xl border border-gray-200 bg-white
                      py-3 pl-10 pr-10 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
                <span className="text-gray-400">to</span>
                <div className="relative flex-1">
                  <Clock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <select
                    value={endHour}
                    onChange={(e) => setEndHour(parseInt(e.target.value))}
                    disabled={isPending}
                    className="
                      w-full appearance-none rounded-xl border border-gray-200 bg-white
                      py-3 pl-10 pr-10 text-sm
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:bg-gray-50 disabled:text-gray-500
                      transition-all duration-200
                    "
                  >
                    {/* 0–24: 24 ends the window at midnight, so 12 AM to Midnight covers the whole day */}
                    {Array.from({ length: 25 }, (_, i) => (
                      <option key={i} value={i}>
                        {i === 24 ? 'Midnight (end of day)' : i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>
              </div>
            </div>

            {/* Days of Week */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Days of Week
              </label>
              <div className="flex gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => handleDayToggle(day.value)}
                    disabled={isPending}
                    className={`
                      flex-1 py-2.5 rounded-lg text-sm font-medium transition-all
                      ${selectedDays.includes(day.value)
                        ? 'bg-guild-green text-white'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-gray-50">
              <div>
                <p className="text-sm font-semibold text-gray-700">Active</p>
                <p className="text-xs text-gray-400">Enable this template immediately</p>
              </div>
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                disabled={isPending}
                className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                  ${isActive ? 'bg-guild-green' : 'bg-gray-300'}
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                <span
                  className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    ${isActive ? 'translate-x-6' : 'translate-x-1'}
                  `}
                />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {isEdit ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE COMPONENT
// ============================================

interface EmptyStateProps {
  searchQuery: string;
  isAdmin: boolean;
  onCreateClick: () => void;
}

function EmptyState({ searchQuery, isAdmin, onCreateClick }: EmptyStateProps) {
  const isSearching = !!searchQuery;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 mb-5">
          <CalendarClock size={32} className="text-gray-300" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {isSearching ? 'No templates found' : 'No templates yet'}
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          {isSearching
            ? `No templates match "${searchQuery}". Try a different search term.`
            : 'Get started by creating your first assessment template to automate spot-check assessments.'}
        </p>

        {isAdmin && !isSearching && (
          <button
            onClick={onCreateClick}
            className="
              mt-6 inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Create First Template
          </button>
        )}
      </div>
    </div>
  );
}
